import { Ok } from 'ts-results-es';
import { z } from 'zod';

import { type BodyOf, FeApi } from '..';
import { SitePreviewSchema, SiteSchema } from '../domain/entities/site';
import type { ICapacityEvaluationRepository } from '../domain/repositories/capacity-evaluation.repository';

import {
  aggregatedResultsMapper,
  jobDetailsMapper,
  jobPreviewMapper,
} from './entities/capacity-evaluation';
import { assetSohDataMapper } from './entities/capacity-evaluation/asset-soh-data.mapper';

export namespace CapacityEvaluationRepositoryMapper {
  export namespace getAll {
    export const outputSchema = z.array(SitePreviewSchema);

    export function bodyFromInput(
      input: ICapacityEvaluationRepository.GetAllInput,
    ): BodyOf<typeof FeApi.capacityEvaluationV1JobListRetrieve> {
      return { siteAccureId: input.siteId, statusType: input.statusType };
    }

    export function outputFromData(
      data: FeApi.ListJobsViewOutputRead,
    ): ICapacityEvaluationRepository.GetAllOutput {
      return data.jobs.map(jobPreviewMapper);
    }
  }

  export namespace getById {
    export const outputSchema = SiteSchema;

    export function bodyFromInput(
      input: ICapacityEvaluationRepository.GetByIdInput,
    ): BodyOf<typeof FeApi.capacityEvaluationV1JobDetailsRetrieve> {
      return { uuid: input.id };
    }

    export function outputFromData(
      data: FeApi.GetJobDetailsViewOutputRead,
    ): ICapacityEvaluationRepository.GetByIdOutput {
      return Ok(jobDetailsMapper(data));
    }
  }

  export namespace getLevels {
    export function bodyFromInput(
      input: ICapacityEvaluationRepository.GetLevelsInput,
    ): BodyOf<typeof FeApi.capacityEvaluationV1JobLevelsRetrieve> {
      return { uuid: input.jobId };
    }

    export function outputFromData(data: {
      levels: string[];
    }): ICapacityEvaluationRepository.GetLevelsOutput {
      return Ok(data.levels);
    }
  }

  export namespace getAggregatedResults {
    export function bodyFromInput(
      input: ICapacityEvaluationRepository.GetAggregatedResultsInput,
    ): BodyOf<typeof FeApi.capacityEvaluationV1JobResultsList> {
      return { uuid: input.id, level: input.level };
    }

    export function outputFromData(
      data: FeApi.GetJobResultsViewOutput[],
    ): ICapacityEvaluationRepository.GetAggregatedResultsOutput {
      return Ok(data.map(aggregatedResultsMapper));
    }
  }

  export namespace create {
    export function bodyFromInput({
      data,
    }: ICapacityEvaluationRepository.CreateInput): BodyOf<
      typeof FeApi.capacityEvaluationV1JobNewCreate
    > {
      return {
        createJobViewInputRequest: {
          accure_id: data.assetId,
          charge_start_time: data.chargeStartTime.toISOString(),
          charge_end_time: data.chargeEndTime.toISOString(),
          charge_energy: data.chargeEnergy,
          charge_power: data.chargePower,
          discharge_start_time: data.dischargeStartTime.toISOString(),
          discharge_end_time: data.dischargeEndTime.toISOString(),
          discharge_energy: data.dischargeEnergy,
          discharge_power: data.dischargePower,
        },
      };
    }

    export function outputFromData(
      data: FeApi.CreateJobViewOutputSerizalizer,
    ): ICapacityEvaluationRepository.CreateOutput {
      return Ok({ id: data.uuid });
    }
  }

  export namespace getSohData {
    export function bodyFromInput(
      input: ICapacityEvaluationRepository.GetSohDataInput,
    ): BodyOf<typeof FeApi.capacityEvaluationV1SiteSohList> {
      return { accureId: input.assetId };
    }

    export function outputFromData(
      data: FeApi.SiteSohViewOutput[],
    ): ICapacityEvaluationRepository.GetSohDataOutput {
      return Ok(data.map(assetSohDataMapper));
    }
  }
}
